import { Box, Center, Column, Spinner, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { useDispatch, useSelector } from 'react-redux';
import { OutlineChevronRightIcon, OutlineShareIcon, OutlineWarning02Icon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { EmptyScreen, LineClamp } from '~/commonComponents';
import { DotsMenu, MenuItem } from '~/commonComponents/DotsMenu';
import { IconButton } from '~/commonComponents/IconButton';
import { useReportIncidentItem } from '~/components/JobList/TodayJobs/ReportIncidentPopup/hooks';
import { incidentTypes } from '~/components/JobList/TodayJobs/ReportIncidentPopup/utils/incidentPopupUtil';
import { InviteButton } from '~/components/ProfessionalsHub/components/InviteButton';
import { getShouldEnforcePermanentJobV2 } from '~/services/posthog/FeatureFlagUtils';
import { useMediaQuery, useUserSelector } from '~/hooks';
import { CalendarTodayFilterIcon } from '~/images/emptyScreenIcons';
import {
  fireTodayShiftViewShiftClicked,
  fireDashboardReportIncidentClicked,
  fireReportIncidentNoShowClicked,
  fireReportIncidentWalkOutClicked,
} from '~/posthogs/events';
import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import {
  FETCH_TODAY_JOBS_ACTION,
  showConfirmReportIncidentPopup,
  showMessage,
} from '../../../actions';
import { createLoadingSelector } from '../../../api/selectors';
import { UserProfession, UserSubProfession } from '../../../enums/UserProfession';

import { Colors } from '../../../themes/colors';
import { useTable } from '../hooks/useTable';
import { ReportIncidentPopup } from './ReportIncidentPopup/ReportIncidentPopup';

const tableHeaderStyle = {
  fontFamily: 'Nunito',
  fontWeight: 'bold',
  fontSize: '18px',
  color: colors.primary[500],
  textAlign: 'left',
};

const tableDataBoldStyle = {
  fontFamily: 'Nunito',
  fontWeight: 700,
  fontSize: 18,
  color: colors.neutral[600],
};

const tableDataNormalStyle = {
  fontFamily: 'Nunito',
  fontWeight: 400,
  fontSize: 16,
  color: colors.neutral[500],
};

export const Table = ({ limit }) => {
  const { jobListScrollRef, onRowClick, onScroll, isLoadingNextPage, isLoadingFirstPage } =
    useTable({ type: 'today' });
  const [isSmallScreen] = useMediaQuery(['lg']);

  const jobList = useSelector((state) => state.job.todayJobs);
  const jobListToMap = limit ? jobList?.slice(0, limit) : jobList;
  const loadingSelector = createLoadingSelector([FETCH_TODAY_JOBS_ACTION]);
  const isLoading = useSelector((state) => loadingSelector(state));

  if (jobListToMap.length === 0 && !isLoading) {
    return (
      <Column pb={16} data-cy="todays-shifts-empty-state">
        <EmptyScreen
          Icon={() => <CalendarTodayFilterIcon />}
          title="No professionals coming in today!"
          description="Request or re-book a professional, and we'll help you bring top dental talent to you!"
          descriptionStyle={{
            textAlign: 'center',
          }}
        />
      </Column>
    );
  }

  if (isLoadingFirstPage) {
    return <CenteredSpinner minHeight={240} />;
  }

  return (
    <Box
      ref={jobListScrollRef}
      onScroll={onScroll}
      style={{
        overflowY: limit ? 'hidden' : 'auto',
        maxHeight: '740px',
        paddingBottom: 12,
      }}
      data-cy="todays-shifts-table-container"
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
        }}
        className="active-shifts-table"
        data-cy="todays-shifts-table"
      >
        <thead
          style={{
            borderBottom: `2px solid ${Colors.neutral_divider}`,
          }}
          data-cy="todays-shifts-table-header"
        >
          <th style={{ opacity: 0, width: 60 }}>#</th>
          <th
            style={{
              ...tableHeaderStyle,
              textAlign: 'left',
              paddingLeft: 16,
            }}
            data-cy="table-header-professional"
          >
            Professional
          </th>
          {!isSmallScreen && (
            <th style={tableHeaderStyle} data-cy="table-header-office">
              Office
            </th>
          )}
          <th style={tableHeaderStyle} data-cy="table-header-shift-details">
            Details
          </th>
          <th style={{ ...tableHeaderStyle, textAlign: 'center' }} data-cy="table-header-invite">
            Rebook
          </th>
          <th style={{ ...tableHeaderStyle, textAlign: 'center' }} data-cy="table-header-report">
            Report
          </th>
          <th style={{ opacity: 0 }}>#</th>
        </thead>
        <tbody data-cy="todays-shifts-table-body">
          {jobListToMap.map((job, index) => (
            <TableItem
              job={job}
              onRowClick={onRowClick}
              isLastItem={index === jobListToMap.length - 1}
            />
          ))}
        </tbody>
      </table>

      {jobListToMap?.length === 0 && !isLoading && (
        <div>
          <p className="font_bold" style={{ textAlign: 'center' }}>
            There are no scheduled shifts for today.
          </p>
        </div>
      )}
      {isLoadingNextPage && (
        <div style={{ marginTop: 20 }}>
          <Spinner centered variant="default" size="md" />
        </div>
      )}
      <ReportIncidentPopup />
    </Box>
  );
};

export const TableItem = ({ job, onRowClick, isLastItem }) => {
  const dispatch = useDispatch();
  const {
    canReport,
    isReported,
    isShiftStartedLessThan30Minutes,
    isShiftNotStartedYet,
    onReportIncidentClick,
  } = useReportIncidentItem(job);
  const [isSmallScreen] = useMediaQuery(['lg']);
  const user = useUserSelector();

  const handleIconClick = (e) => {
    e.stopPropagation();
    if (isShiftStartedLessThan30Minutes) {
      dispatch(
        showMessage({
          title: 'Please Wait Before Reporting',
          message: (
            <>
              Reporting is unavailable for the first 30 minutes after the shift starts. If the
              professional still hasn’t arrived after that, please return to submit a report. Thank
              you for your patience.
            </>
          ),
          popupProps: {
            rightButtonText: 'Close',
            descriptionStyle: {
              marginBottom: 40,
              width: 420,
            },
          },
        }),
      );
      return;
    }
  };

  const getIconColor = (isHovered) => {
    if (isReported) {
      return colors.warning[500];
    }

    if (isShiftNotStartedYet) {
      return colors.neutral[200];
    }

    if (isHovered) {
      return colors.warning[500];
    }

    return colors.neutral[200];
  };

  const getTooltipText = () => {
    if (isReported) {
      return 'Reported';
    }
    return 'Report Incident';
  };

  return (
    <>
      <tr
        key={job.id}
        style={{
          cursor: 'pointer',
          borderBottom: `2px solid ${Colors.neutral_divider}`,
          paddingBottom: 8,
        }}
        onClick={() => {
          fireTodayShiftViewShiftClicked();
          onRowClick(job);
        }}
        data-cy="shift-table-row"
        data-job-id={job.id}
      >
        <td>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <img
              src={job.professional?.image}
              style={{
                width: 60,
                height: 60,
                objectFit: 'contain',
                borderRadius: 60,
                border: `1px solid ${colors.neutral[100]}`,
              }}
              alt=""
              data-cy="professional-avatar"
            />
          </div>
        </td>
        <td width="20%" style={{ paddingRight: '10px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'left',
              marginLeft: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              data-cy="professional-info"
            >
              <span
                style={tableDataBoldStyle}
                data-cy="professional-name"
              >{`${job.professional?.full_name}`}</span>
              <span style={tableDataNormalStyle} data-cy="professional-role">
                {`${
                  UserSubProfession.getName(job.subProfession) ??
                  UserProfession.getName(job.profession)
                }`}
              </span>
            </div>
          </div>
        </td>
        {!isSmallScreen && (
          <td width="30%" style={{ paddingRight: 24 }}>
            <LineClamp
              lineClamp={2}
              text={job.offer_owner.office}
              style={{ ...tableDataNormalStyle, paddingRight: 10 }}
              data-cy="office-name"
            />
          </td>
        )}
        <td width="20%">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}
            data-cy="shift-details"
          >
            {getShouldEnforcePermanentJobV2() && (
              <Box
                // Intrinsic pill styling — tight padding + radius/colour below the DS
                // spacing scale, so kept inline rather than snapped to a coarser token.
                style={{
                  alignSelf: 'flex-start',
                  padding: '2px 10px',
                  marginBottom: 5,
                  borderRadius: 8,
                  backgroundColor: job?.isOnsiteInterview
                    ? Colors.jobs_interview_background
                    : Colors.jobs_shift_background,
                }}
              >
                <Typography as="span" color={colors.neutral[500]}>
                  {job?.isOnsiteInterview ? 'INTERVIEW' : 'SHIFT'}
                </Typography>
              </Box>
            )}
            <span
              style={{
                ...tableDataBoldStyle,
                fontSize: 16,
                color: colors.neutral[500],
              }}
              data-cy="shift-date"
            >
              {job.shift?.local_date}
            </span>
            <span style={tableDataNormalStyle} data-cy="shift-time">
              {`${job.shift?.local_start_time} - ${job.shift?.local_end_time}`}
            </span>
          </div>
        </td>
        <td>
          <Center>
            {!job?.isOnsiteInterview && (
              <InviteButton
                user={{
                  id: job.professional._id,
                  image: job.professional.image,
                  name: job.professional.full_name,
                  profession: job.profession,
                  professions: [job.profession],
                  officeId: job.offer_owner._id,
                }}
                position="bottom"
                source="dashboard"
                shiftId={job.id || job._id}
                CustomMenuIcon={({ isHovered }) => {
                  return (
                    <Center>
                      <IconButton
                        Icon={
                          <OutlineShareIcon
                            color={isHovered ? colors.secondary[300] : colors.secondary[500]}
                          />
                        }
                        tooltipContent="Rebook Me!"
                        buttonStyle={{
                          height: 48,
                          width: 48,
                        }}
                      />
                    </Center>
                  );
                }}
              />
            )}
          </Center>
        </td>
        <td>
          <Center>
            <DotsMenu
              position="bottom"
              CustomMenuIcon={({ isHovered, isDisabled }) => (
                <IconButton
                  Icon={<OutlineWarning02Icon color={getIconColor(isHovered)} />}
                  onClick={
                    !canReport ? handleIconClick : () => fireDashboardReportIncidentClicked()
                  }
                  tooltipContent={getTooltipText()}
                  hideTooltip={isShiftNotStartedYet}
                  disabled={isDisabled}
                  buttonStyle={{
                    width: 48,
                    height: 48,
                  }}
                />
              )}
              disabled={isReported || isShiftNotStartedYet}
              dataCy="report-incident-button"
            >
              <MenuItem
                text="Report No-Show"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  fireReportIncidentNoShowClicked();
                  onReportIncidentClick();
                  dispatch(showConfirmReportIncidentPopup({ incidentType: incidentTypes.noShow }));
                }}
                dataCy="report-no-show-option"
              />
              <MenuItem
                text="Report Walk-Out"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  fireReportIncidentWalkOutClicked();
                  onReportIncidentClick();
                  dispatch(
                    showConfirmReportIncidentPopup({ incidentType: incidentTypes.walkedOut }),
                  );
                }}
                dataCy="report-walked-out-option"
              />
            </DotsMenu>
          </Center>
        </td>
        <td>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <OutlineChevronRightIcon size={10} color={colors.secondary[500]} />
          </div>
        </td>
      </tr>
    </>
  );
};
